import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter } from "@astryxdesign/core/Layout";
import { HStack } from "@astryxdesign/core/HStack";
import { VStack } from "@astryxdesign/core/VStack";
import { Button } from "@astryxdesign/core/Button";
import { Text } from "@astryxdesign/core/Text";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { useToast } from "@astryxdesign/core/Toast";
import { Inbox } from "lucide-react";
import { api } from "../../lib/api";
import { RetryBanner } from "../../components/RetryBanner";

interface PendingRequestsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

// Placeholder row count while the queue loads — this dialog opens to a
// mostly-empty room far more often than a full one, so two rows (rather than
// guessing at the eventual count) is enough to say "loading" without
// promising a queue depth that isn't real yet.
const LOADING_ROW_COUNT = 2;

/**
 * The approver's queue: viewers waiting for a slot to be freed.
 *
 * Each row states the whole trade in one line — who asked, what they want, and
 * what would stop — so it can be answered without reconstructing the
 * situation. That is the point of the request carrying both camera names
 * rather than being recomputed at approval time: the approver acts on what the
 * requester actually asked for, not on whatever the registry would nominate
 * some minutes later.
 */
export function PendingRequestsDialog({ isOpen, onOpenChange }: PendingRequestsDialogProps) {
  const queryClient = useQueryClient();
  const showToast = useToast();

  const {
    data: requests,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["stream-requests"],
    queryFn: api.streamRequests.list,
    enabled: isOpen,
    // Requests expire after a few minutes and colleagues answer them too, so a
    // queue that went stale while somebody read it would offer actions that no
    // longer exist.
    refetchInterval: 5_000,
  });

  const answer = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      approve ? api.streamRequests.approve(id) : api.streamRequests.reject(id),
    onSuccess: async (_data, variables) => {
      showToast({ body: variables.approve ? "Slot approved" : "Request declined" });
      await queryClient.invalidateQueries({ queryKey: ["stream-requests"] });
      await queryClient.invalidateQueries({ queryKey: ["streams-active"] });
    },
    onError: (err: unknown) => {
      // Answering twice, or answering something that has expired, is normal in
      // a room where several people can act. Say so plainly rather than
      // presenting it as a failure.
      showToast({ type: "error", body: err instanceof Error ? err.message : "Couldn't answer that request." });
    },
  });

  const pending = requests ?? [];

  let body: ReactNode;
  if (isLoading) {
    // A previous "Loading…" EmptyState title read identically to a real
    // empty queue for the split second before data arrives, and — worse —
    // was indistinguishable from the failure case below, which also
    // rendered as "Nothing waiting" before this fix. This is this app's
    // usual skeleton treatment, matching every other list in it.
    body = (
      <VStack gap={3}>
        {Array.from({ length: LOADING_ROW_COUNT }, (_, i) => (
          // eslint-disable-next-line react/no-array-index-key -- fixed-count static placeholders, never reordered
          <VStack key={i} gap={1.5}>
            <Skeleton index={i * 2} height={18} width="65%" />
            <Skeleton index={i * 2 + 1} height={14} width="90%" />
          </VStack>
        ))}
      </VStack>
    );
  } else if (error) {
    // This dialog IS the escalation path's own queue — an approver who
    // can't tell "the queue failed to load" from "nobody is waiting" is
    // being told, incorrectly, that nobody needs them. Previously a failed
    // fetch rendered the exact same "Nothing waiting" EmptyState as a
    // genuinely empty queue.
    body = (
      <RetryBanner
        title="Couldn't load requests"
        description={error instanceof Error ? error.message : "Unknown error"}
        onRetry={() => void refetch()}
        isRetrying={isRefetching}
      />
    );
  } else if (pending.length === 0) {
    body = (
      <EmptyState
        icon={<Inbox />}
        title="Nothing waiting"
        description="Requests appear here and expire after a few minutes."
      />
    );
  } else {
    body = (
      <VStack gap={3}>
        {pending.map((req) => (
          <VStack key={req.id} gap={1}>
            <Text weight="medium">
              {req.requester_name || "An operator"} wants {req.camera_name}
            </Text>
            <Text type="supporting" color="secondary">
              This would stop {req.evict_camera_name}, and everyone watching it would be told.
            </Text>
            <HStack gap={2}>
              <Button
                label="Approve"
                variant="primary"
                size="sm"
                onClick={() => answer.mutate({ id: req.id, approve: true })}
                isLoading={answer.isPending}
              />
              <Button
                label="Decline"
                variant="ghost"
                size="sm"
                onClick={() => answer.mutate({ id: req.id, approve: false })}
                isLoading={answer.isPending}
              />
            </HStack>
          </VStack>
        ))}
      </VStack>
    );
  }

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} purpose="info" width={560}>
      <Layout
        header={
          <DialogHeader
            title="Slot requests"
            subtitle="Operators waiting for a stream to be stopped so theirs can start."
            onOpenChange={onOpenChange}
          />
        }
        content={<LayoutContent>{body}</LayoutContent>}
        footer={
          <LayoutFooter>
            <HStack gap={2} justify="end">
              <Button label="Close" variant="ghost" onClick={() => onOpenChange(false)} />
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}
